import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import { CONTACT } from "../data/site";
import { Reveal, Label } from "../components/ui-kit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const field = "w-full bg-black/50 border border-white/15 rounded-xl px-4 py-3 text-white placeholder:text-white/30 outline-none focus:border-gold focus:ring-2 focus:ring-gold/40 transition";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill in your name, email and message.");
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: MapPin, label: "Visit us", value: CONTACT.address },
    { icon: Phone, label: "Call us", value: CONTACT.phone, href: `tel:${CONTACT.phone}` },
    { icon: Mail, label: "Email us", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: Clock, label: "Hours", value: CONTACT.hoursShort },
  ];

  return (
    <>
      <section className="pt-40 pb-16 bg-black" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Label>Get in touch</Label>
          <h1 className="font-display uppercase text-white text-5xl md:text-8xl leading-[0.9] mt-6">Let's talk.</h1>
          <p className="mt-6 text-lg text-lightgrey max-w-2xl">Have a question or ready to book your free intro? Drop us a line — a real human will get back to you.</p>
        </div>
      </section>

      <section className="bg-black pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12">
          <Reveal>
            <form onSubmit={submit} className="rounded-3xl border border-white/10 bg-charcoal p-8 md:p-10 space-y-4" data-testid="contact-form">
              <h2 className="font-display text-3xl text-white uppercase">Send a message</h2>
              <input className={field} placeholder="Full name*" value={form.name} onChange={set("name")} data-testid="contact-name" />
              <div className="grid sm:grid-cols-2 gap-4">
                <input className={field} type="email" placeholder="Email*" value={form.email} onChange={set("email")} data-testid="contact-email" />
                <input className={field} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="contact-phone" />
              </div>
              <textarea className={field + " resize-none"} rows={5} placeholder="How can we help?*" value={form.message} onChange={set("message")} data-testid="contact-message" />
              <button type="submit" disabled={loading} className="w-full rounded-full bg-gold text-black font-bold py-4 hover:bg-white transition disabled:opacity-60" data-testid="contact-submit">
                {loading ? "Sending…" : "Send Message"}
              </button>
            </form>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="space-y-4">
              {info.map((it) => (
                <div key={it.label} className="rounded-2xl border border-white/10 bg-charcoal p-6 flex gap-4 items-start">
                  <span className="w-11 h-11 rounded-xl bg-gold/15 border border-gold/30 flex items-center justify-center text-gold shrink-0"><it.icon size={20} /></span>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-white/40">{it.label}</p>
                    {it.href ? <a href={it.href} className="text-white hover:text-gold transition break-all">{it.value}</a> : <p className="text-white">{it.value}</p>}
                  </div>
                </div>
              ))}
              <div className="rounded-2xl overflow-hidden border border-white/10 h-64">
                <iframe title="HFC location" src={CONTACT.mapEmbed} className="w-full h-full grayscale" loading="lazy" data-testid="contact-map" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
